<template>
    <div>
        <b-button class="right" variant="primary" @click="isModalOpen=true">
            Dateien hinzufügen
        </b-button>
        <b-modal v-model="isModalOpen" hide-footer size="lg"
            title="Dateien hochladen" @hide="reset()">
            <div class="record-document-upload-modal loading-content-container">
                <div v-if="queueIsProcessing" class="loading-overlay d-flex justify-content-center pt-5">
                    <csp-loading-indicator/>
                </div>
                <div class="form-inline form-inline-stacked">
                    <csp-form-group-select-searchable
                        ref="categoryGroupSelect"
                        class="col-4"
                        :form="fileUploadForm"
                        :options-loader="loadFileCategoryGroupOptions"
                        override-label="Zuordnung"
                        property="categoryGroupId"
                        type="autocomplete"
                        @changeOption="selectedCategoryGroup = $event"/>
                    <csp-form-group-select-searchable
                        ref="categorySelect"
                        :auto-select-single-option="true"
                        class="col-4"
                        :dependencies="['categoryGroupId']"
                        :form="fileUploadForm"
                        :options-loader="loadFileCategoryOptions"
                        override-label="Kategorie"
                        property="categoryId"
                        type="autocomplete"
                        @changeOption="onCategorySelected($event)"/>

                    <csp-hide-for-group
                        class="col-4"
                        :damage-record-note-id="damageRecordNoteId"
                        :form="fileUploadForm"
                        :is-loading-from-parent="isLoadingHideSettings > 0"
                        :mandatory-hide-for-group="mandatoryHideForGroup"
                        property="hideForGroup"
                        :record-id="recordId"/>
                </div>
                <csp-base-file-upload
                    ref="fileUpload"
                    :auto-process-queue="false"
                    :before-file-added="validateUploadPreconditions"
                    :block-upload="!categorySelected"
                    class="multi-file-upload"
                    file-input-name="fileUpload"
                    :multiple-upload="true"
                    :title="$t('record document upload')"
                    :upload-error-message="$t('record document upload error')"
                    :upload-url="uploadUrl"
                    @after-files-added="handleAfterFilesAdded"
                    @before-upload="handleBeforeUpload"
                    @file-added="handleFileAdded"
                    @file-removed="handleFileRemoved"
                    @file-upload-complete="handleFileUploadComplete"
                    @file-upload-progress="handleFileUpdate"
                    @total-upload-progress="handleTotalUploadProgress"
                    @upload-complete="handleUploadComplete"
                    @upload-disabled="handleUploadDisabled">
                    <slot/>
                </csp-base-file-upload>
                <b-button class="w-100 my-2"
                    :disabled="filesForUpload.length === 0"
                    size="sm"
                    variant="primary"
                    @click="processQueue">
                    Dateien hochladen
                </b-button>
                <div class="file-preview-container mt-2">
                    <b-table v-if="filesForUpload.length > 0" :fields="previewTableFields" :items="filesForUpload">
                        <template v-slot:table-colgroup="scope">
                            <col
                                v-for="field in scope.fields"
                                :key="field.key"
                                :style="{ width: field.width ?? 'auto' }">
                        </template>
                        <template v-slot:cell(actions)="data">
                            <div class="table-actions" style="text-align: right">
                                <span class="file-remove" @click="removeFile(data.item.file)">
                                    <i class="inticon-remove"/>
                                </span>
                            </div>
                        </template>
                    </b-table>
                </div>
                <div v-if="queueIsProcessing" class="upload-progress my-3">
                    <b-progress max="100" show-progress :value="totalUploadProgress"/>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import Form from '@form/Form';
    import '@desktop/scss/components/record/document-upload-modal.scss';

    const container = Container.getInstance();
    const ajaxService = container.getAjaxService();
    const recordRepository = container.getDesktopRecordRepository();
    const flashMessenger = container.getFlashMessenger();

    export default {
        props: {
            recordId: {
                type: Number,
                required: true,
            },
            damageRecordNoteId: {
                type: Number,
                default: null,
            },
        },
        emits: ['upload-success'],
        data() {
            const fileUploadForm = new Form({
                categoryGroupId: null,
                categoryId: null,
                hideForGroup: null,
            });
            return {
                fileUploadForm: fileUploadForm,
                // Role groups that are forced-hidden for the currently selected file category.
                mandatoryHideForGroup: [],
                // The user's own hide-for-group selection at the time forced groups were last applied.
                // Used to compute which forced groups were net-new so only those are removed on category change.
                userHideValues: null,
                uploadNotificationSessionId: this.generateUploadNotificationSessionId(),
                queueIsProcessing: false,
                isLoadingHideSettings: 0,
                isModalOpen: false,
                filesForUpload: [],
                totalUploadBytes: 0,
                totalBytesSent: 0,
                totalUploadProgress: 0,
                previewTableFields: [
                    {
                        key: 'categoryGroupName',
                        label: 'Zuordnung',
                        width: '50px',
                    },
                    {
                        key: 'categoryName',
                        label: 'Kategorie',
                        width: '50px',
                    },
                    {
                        key: 'fileName',
                        label: 'Dateiname',
                        width: '50px',
                    },
                    {
                        key: 'actions',
                        label: '',
                        width: '50px',
                    },
                ],
            }
        },
        computed: {
            categorySelected() {
                return !!this.fileUploadForm.getDataOfProperty('categoryGroupId')
                    && !!this.fileUploadForm.getDataOfProperty('categoryId');
            },
            uploadUrl() {
                let url = ''
                if (this.damageRecordNoteId) {
                    url = '/de/Schaden-Manager/Akte/ajax/type/noteCodeFileUpload/vehicleDamageRecordId/'
                        + this.recordId +
                        '/damageRecordNoteId/'
                        + this.damageRecordNoteId;
                } else {
                    url = '/de/Schaden-Manager/Akte/fileupload/vehicleDamageRecordId/' + this.recordId;
                }
                return ajaxService.addSessionIdQueryParameter(url);
            },
        },
        methods: {
            generateUploadNotificationSessionId() {
                return 'upload-notification-' + Math.random().toString(36).substring(2, 15)
                    + Math.random().toString(36).substring(2, 15);
            },
            getFileMetaForFile(file) {
                return this.filesForUpload.find(f => f.uuid === file.upload.uuid);
            },
            loadFileCategoryGroupOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'fileCategoryGroups',
                    {recordId: this.recordId}
                );
            },
            loadFileCategoryOptions() {
                return recordRepository.loadLegacySelectOptions(
                    'fileCategory',
                    {
                        fileCategoryGroupId: this.fileUploadForm.getProperty('categoryGroupId'),
                        recordId: this.recordId,
                    }
                );
            },
            onCategorySelected(category) {
                this.isLoadingHideSettings++;
                this.selectedCategory = category;
                if (!category) {
                    this.updateForcedHideForGroup([]);
                    this.isLoadingHideSettings--;
                    return;
                }
                const requestedCategoryValue = category.value;
                recordRepository.hideForGroupMandatoryForFile(
                    this.recordId,
                    {fileCategoryId: category.value}
                ).then((response) => {
                    // Discard the response if the category has changed since the request was launched.
                    if (this.selectedCategory?.value !== requestedCategoryValue) {
                        return;
                    }
                    this.updateForcedHideForGroup(response.data.attributes.mandatoryHideGroups);
                }).finally(() => {
                    this.isLoadingHideSettings--;
                });
            },
            // Removes previously forced groups, then applies the new forced groups on top.
            // Passing an empty array clears any previously forced groups.
            updateForcedHideForGroup(groups) {
                // Compute which of the previously forced groups were net-new additions (not already
                // checked by the user). Only those should be removed — groups the user had checked
                // independently must survive even if they were also in the previous forced set.
                const current = this.fileUploadForm.getProperty('hideForGroup') ?? [];
                const netForced = this.userHideValues !== null
                    ? this.mandatoryHideForGroup.filter((g) => !this.userHideValues.includes(g))
                    : this.mandatoryHideForGroup;
                const withoutNetForced = current.filter((g) => !netForced.includes(g));
                this.userHideValues = groups.length > 0 ? [...withoutNetForced] : null;
                this.mandatoryHideForGroup = groups;
                const merged = [...new Set([...withoutNetForced, ...groups])];
                this.fileUploadForm.setProperty('hideForGroup', merged);
            },
            processQueue() {
                this.queueIsProcessing = true;
                this.$refs.fileUpload.getDropzoneInstance().processQueue();
            },
            handleFileAdded(file) {
                let fileUploadData = {
                    uuid: file.upload.uuid,
                    file,
                    hideForGroup: this.fileUploadForm.getDataOfProperty('hideForGroup'),
                    fileName: file.name,
                }

                const categoryId = this.fileUploadForm.getDataOfProperty('categoryId');
                const categoryGroupId = this.fileUploadForm.getDataOfProperty('categoryGroupId');

                if (!categoryId || !categoryGroupId) return;
                fileUploadData.categoryId = categoryId;
                fileUploadData.categoryGroupId = categoryGroupId;
                fileUploadData.categoryName = this.selectedCategory.text
                fileUploadData.categoryGroupName = this.selectedCategoryGroup.text

                this.filesForUpload.push(fileUploadData);
            },
            handleFileRemoved(file) {
                this.filesForUpload = this.filesForUpload.filter(f => f.uuid !== file.upload.uuid);
            },
            handleFileUpdate(file) {
                const fileWithMeta = this.getFileMetaForFile(file)
                if (!fileWithMeta) {
                    return;
                }
                fileWithMeta.file = file;
            },
            handleFileUploadComplete(file) {
                this.handleFileUpdate(file);
            },
            handleTotalUploadProgress() {
                const dropzone = this.$refs.fileUpload.getDropzoneInstance();
                // Compute the sum of all bytes sent so far, including completed uploads.
                // Dropzone stops counting "completed" files in total progress.
                let bytesSent = dropzone.files.reduce((sum, file) => {
                    return sum + (file.upload?.bytesSent || 0);
                }, 0);
                // Some browsers/servers may report a few extra bytes after compression or chunking,
                bytesSent = Math.min(bytesSent, this.totalUploadBytes);
                // Compute the percentage progress
                this.totalUploadProgress = this.totalUploadBytes
                    ? Math.round((bytesSent / this.totalUploadBytes) * 100)
                    : 0;
            },
            handleBeforeUpload(file, xhr, formData) {
                const fileWithMeta = this.getFileMetaForFile(file);

                formData.append('name', file.name);
                if (this.damageRecordNoteId) {
                    formData.append('fileCategory', fileWithMeta.categoryId);
                    formData.append('fileCategoryGroupId', fileWithMeta.categoryGroupId);
                } else {
                    formData.append('subformFileCategory[fileCategoryGroupId]', fileWithMeta.categoryGroupId);
                    formData.append('subformFileCategory[fileCategory]', fileWithMeta.categoryId);
                }
                formData.append('uploadNotificationSessionId', this.uploadNotificationSessionId);
                fileWithMeta.hideForGroup.forEach((groupId) => {
                    formData.append('visibility[hide][]', groupId);
                });
            },
            handleUploadDisabled() {
                flashMessenger.addWarning(this.$t('record document upload error no category'));
            },
            handleUploadComplete(data, statistics) {
                this.queueIsProcessing = false

                this.$emit('upload-success', data);
                let translationKey = 'record document upload success';

                if (statistics.numberOfAddedFiles > 1) {
                    translationKey = translationKey + ' multiple';
                }

                if (statistics.numberOfSuccessfullyUploadedFiles === statistics.numberOfAddedFiles) {
                    flashMessenger.addSuccess(this.$t(translationKey, statistics))
                    this.reset();
                    this.isModalOpen = false;
                    return;
                }

                flashMessenger.addWarning(this.$t(translationKey, statistics));
            },
            removeFile(file) {
                this.$refs.fileUpload.getDropzoneInstance().removeFile(file);
            },
            reset() {
                this.fileUploadForm.reset();
                this.mandatoryHideForGroup = [];
                this.userHideValues = null;
                this.uploadNotificationSessionId = this.generateUploadNotificationSessionId();
                this.totalUploadProgress = 0;
                this.queueIsProcessing = false;
                this.$refs.fileUpload.reset();
            },
            resetCategory() {
                this.fileUploadForm.setProperty('categoryId', null);
                this.fileUploadForm.setProperty('categoryGroupId', null);
            },
            validateUploadPreconditions() {
                const valid = this.categorySelected;
                if (!this.categorySelected) {
                    flashMessenger.addWarning(this.$t('record document upload error no category'));
                }
                return valid;
            },
            handleAfterFilesAdded(files) {
                this.totalUploadBytes = Array.from(files).reduce((sum, file) => sum + file.size, 0);
                this.resetCategory();
            },
        },
    }
</script>
